import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useWebPhoneV2, WebPhoneCredentials, ConnectionStatus, CallStatus, CallSession, RTCStats } from '../hooks/useWebPhoneV2';
import api from '../services/api';

interface WebPhoneContextV2Type {
  isWindowOpen: boolean;
  isMinimized: boolean;
  connectionStatus: ConnectionStatus;
  callStatus: CallStatus;
  currentCall: CallSession | null;
  openWindow: () => void;
  closeWindow: () => void;
  minimizeWindow: () => void;
  restoreWindow: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  openPictureInPicture: () => Promise<void>;
  // Forward all webphone functions
  makeCall: (number: string) => void;
  answerCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  isMuted: boolean;
  isOnHold: boolean;
  dialedNumber: string;
  addDigit: (digit: string) => void;
  deleteDigit: () => void; // NEW: Delete last digit
  clearNumber: () => void;
  errorMessage: string | null;
  // Audio device management
  audioDevices: { inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] };
  selectedSpeaker: string;
  selectedMicrophone: string;
  volume: number;
  changeSpeaker: (deviceId: string) => void;
  changeMicrophone: (deviceId: string) => void;
  changeVolume: (volume: number) => void;
  enumerateDevices: () => Promise<void>;
  testSpeaker: () => Promise<void>;
  // RTC Statistics
  rtcStats: RTCStats | null;
  // Event Logs
  eventLogs: Array<{ timestamp: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }>;
  // Debug Mode
  debugMode: boolean;
  toggleDebugMode: () => void;
}

const WebPhoneContextV2 = createContext<WebPhoneContextV2Type | undefined>(undefined);

export const useWebPhoneContextV2 = () => {
  const context = useContext(WebPhoneContextV2);
  if (!context) {
    throw new Error('useWebPhoneContextV2 must be used within WebPhoneProviderV2');
  }
  return context;
};

export const WebPhoneProviderV2: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [credentials, setCredentials] = useState<WebPhoneCredentials | null>(null);
  const hasAutoConnectedRef = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log('[WebPhoneContextV2] State changed - isWindowOpen:', isWindowOpen, 'isMinimized:', isMinimized);
  }, [isWindowOpen, isMinimized]);

  const webphone = useWebPhoneV2();

  // Fetch credentials from API
  const fetchCredentials = useCallback(async () => {
    try {
      console.log('[WebPhoneV2] Fetching credentials from API...');
      console.log('[WebPhoneV2] API endpoint: /endpoints/webphone-credentials');

      // Check if we have an auth token before making the request
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      console.log('[WebPhoneV2] Auth token present:', !!token);
      if (token) {
        console.log('[WebPhoneV2] Token preview:', token.substring(0, 30) + '...');
      } else {
        console.error('[WebPhoneV2] NO TOKEN FOUND - API call will likely fail or hang');
      }

      console.log('[WebPhoneV2] About to call api.get...');
      const response = await api.get('/endpoints/webphone-credentials');
      console.log('[WebPhoneV2] API call returned, checking response...');
      console.log('[WebPhoneV2] Response status:', response.status);
      console.log('[WebPhoneV2] Response data:', response.data);
      if (response.data.success) {
        console.log('[WebPhoneV2] Success=true, extracting credentials...');
        setCredentials(response.data.data);
        console.log('[WebPhoneV2] Credentials fetched successfully:', response.data.data);
        return response.data.data;
      }
      console.error('[WebPhoneV2] Success=false, throwing error');
      throw new Error(response.data.message || 'Failed to fetch credentials');
    } catch (error: any) {
      console.error('[WebPhoneV2] CATCH BLOCK - Failed to fetch credentials:', error);
      console.error('[WebPhoneV2] Error name:', error.name);
      console.error('[WebPhoneV2] Error message:', error.message);
      console.error('[WebPhoneV2] Error response:', error.response);
      console.error('[WebPhoneV2] Error details:', error.response?.data || error.message);
      throw error;
    }
  }, []);

  // Connect to WebRTC
  const connect = useCallback(async () => {
    try {
      console.log('[WebPhoneV2] Connect called, current status:', webphone.connectionStatus);
      let creds = credentials;
      if (!creds) {
        console.log('[WebPhoneV2] No cached credentials, fetching from API...');
        creds = await fetchCredentials();
        console.log('[WebPhoneV2] fetchCredentials() returned:', creds ? 'GOT CREDENTIALS' : 'NULL/UNDEFINED');
      } else {
        console.log('[WebPhoneV2] Using cached credentials');
      }
      if (creds) {
        console.log('[WebPhoneV2] Connecting to SBC with credentials:', { username: creds.username, sbcServers: creds.sbcServers });
        await webphone.connect(creds);
        console.log('[WebPhoneV2] webphone.connect() completed');
      } else {
        console.error('[WebPhoneV2] No credentials available - creds is null/undefined');
      }
    } catch (error) {
      console.error('[WebPhoneV2] CONNECT CATCH BLOCK - Connect failed:', error);
      console.error('[WebPhoneV2] Error details:', error);
    }
  }, [credentials, fetchCredentials, webphone]);

  // Connect when window opens
  useEffect(() => {
    if (isWindowOpen && webphone.connectionStatus === 'disconnected' && !hasAutoConnectedRef.current) {
      console.log('[WebPhoneV2] Window opened - connecting...');
      hasAutoConnectedRef.current = true;
      connect();
    } else if (!isWindowOpen) {
      // Reset the flag when window closes so next open will connect
      hasAutoConnectedRef.current = false;
    }
  }, [isWindowOpen, webphone.connectionStatus, connect]);

  const openWindow = useCallback(() => {
    console.log('[WebPhoneContextV2] openWindow called');
    setIsWindowOpen(true);
    setIsMinimized(false);
  }, []);

  const closeWindow = useCallback(() => {
    console.log('[WebPhoneContextV2] closeWindow called');
    setIsWindowOpen(false);
    setIsMinimized(false);
    webphone.disconnect();
  }, [webphone]);

  const minimizeWindow = useCallback(() => {
    console.log('[WebPhoneContextV2] minimizeWindow called');
    setIsMinimized(true);
  }, []);

  const restoreWindow = useCallback(() => {
    console.log('[WebPhoneContextV2] restoreWindow called');
    setIsMinimized(false);
  }, []);

  const openPictureInPicture = useCallback(async () => {
    try {
      const width = 340;
      const height = 600;
      const left = window.screen.width - width - 20;
      const top = 80;

      const popup = window.open(
        '/webphone-popup-v2',
        'WebPhoneV2',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
      );

      if (popup) {
        console.log('[WebPhoneV2] Popup window opened');
      } else {
        alert('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      console.error('[WebPhoneV2] Failed to open popup:', error);
      alert('Failed to open WebPhone window.');
    }
  }, []);

  const value: WebPhoneContextV2Type = {
    isWindowOpen,
    isMinimized,
    connectionStatus: webphone.connectionStatus,
    callStatus: webphone.callStatus,
    currentCall: webphone.currentCall,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    connect,
    disconnect: webphone.disconnect,
    openPictureInPicture,
    makeCall: webphone.makeCall,
    answerCall: webphone.answerCall,
    hangUp: webphone.hangUp,
    toggleMute: webphone.toggleMute,
    toggleHold: webphone.toggleHold,
    isMuted: webphone.isMuted,
    isOnHold: webphone.isOnHold,
    dialedNumber: webphone.dialedNumber,
    addDigit: webphone.addDigit,
    deleteDigit: webphone.deleteDigit, // NEW
    clearNumber: webphone.clearNumber,
    errorMessage: webphone.errorMessage,
    audioDevices: webphone.audioDevices,
    selectedSpeaker: webphone.selectedSpeaker,
    selectedMicrophone: webphone.selectedMicrophone,
    volume: webphone.volume,
    changeSpeaker: webphone.changeSpeaker,
    changeMicrophone: webphone.changeMicrophone,
    changeVolume: webphone.changeVolume,
    enumerateDevices: webphone.enumerateDevices,
    testSpeaker: webphone.testSpeaker,
    rtcStats: webphone.rtcStats,
    eventLogs: webphone.eventLogs,
    debugMode: webphone.debugMode,
    toggleDebugMode: webphone.toggleDebugMode,
  };

  return <WebPhoneContextV2.Provider value={value}>{children}</WebPhoneContextV2.Provider>;
};
